import { useState } from "react";
import api from "../../lib/api";
import { canWrite } from "../../lib/billing";
import { CreditNoteDialog, InvoiceDialog, PaymentDialog, ReasonDialog, RefundDialog, ReminderDialog } from "./BillingDialogs";

// The row actions of installments, invoices / credit notes and payments, from the person's own rights (the server checks again),
// and the dialogs they open. Shared by a subscription's page and « Factures et échéances ». `reload` runs after every write.
export default function useBillingActions(user, reload) {
  const [dialog, setDialog] = useState(null);
  const canInvoice = canWrite(user, "billing:manage_invoices");
  const canPay = canWrite(user, "billing:record_payment");
  const close = () => setDialog(null);
  const done = () => { setDialog(null); reload(); };

  const installmentActions = (i) => {
    const open = i.state !== "cancelled" && i.remaining_minor > 0;
    const out = [];
    if (canInvoice && open && !i.invoice_id) out.push({ id: "invoice", label: "Consigner la facture", onSelect: () => setDialog({ kind: "invoice", items: [i] }) });
    if (canPay && open) out.push({ id: "pay", label: "Consigner un encaissement", onSelect: () => setDialog({ kind: "pay", target: { installment: i } }) });
    if (canInvoice && open) out.push({ id: "remind", label: "Consigner une relance", onSelect: () => setDialog({ kind: "remind", installment: i }) });
    if (canInvoice && i.state !== "cancelled" && !i.invoice_id && !i.paid_minor) {
      out.push({ id: "cancel", label: "Annuler l'échéance", danger: true, onSelect: () => setDialog({ kind: "cancel-installment", installment: i }) });
    }
    return out;
  };

  const documentActions = (d) => {
    if (d.status !== "active") return [];
    const out = [];
    if (d.kind === "invoice" && canPay) {
      out.push({ id: "pay", label: "Consigner un encaissement", onSelect: () => payInvoiceTarget(d).catch(() => setDialog(null)) });
    }
    if (d.kind === "invoice" && canInvoice) out.push({ id: "credit", label: "Consigner un avoir", onSelect: () => setDialog({ kind: "credit", invoice: d }) });
    if (canInvoice) out.push({ id: "cancel", label: d.kind === "invoice" ? "Annuler la facture" : "Annuler l'avoir", danger: true, onSelect: () => setDialog({ kind: "cancel-document", document: d }) });
    return out;
  };

  const paymentActions = (p) => {
    if (!canPay || p.status !== "active") return [];
    const out = [];
    if (p.net_minor > 0) out.push({ id: "refund", label: "Consigner un remboursement", onSelect: () => setDialog({ kind: "refund", payment: p }) });
    out.push({ id: "cancel", label: "Annuler l'encaissement", danger: true, onSelect: () => setDialog({ kind: "cancel-payment", payment: p }) });
    return out;
  };

  // Paying an invoice settles what remains on its installments: the page knows them (or asks the server).
  const payInvoiceTarget = async (invoice) => {
    const { data } = await api.get("/billing/installments", { params: { subscription_id: invoice.subscription_id, limit: 200 } });
    const remaining = data.items.filter((i) => invoice.installment_ids.includes(i.id)).reduce((s, i) => s + i.remaining_minor, 0);
    setDialog({ kind: "pay", target: { invoice, remaining_minor: remaining } });
  };

  const dialogs = (
    <>
      {dialog?.kind === "invoice" && <InvoiceDialog installments={dialog.items} onOpenChange={close} onDone={done} />}
      {dialog?.kind === "pay" && <PaymentDialog target={dialog.target} onOpenChange={close} onDone={done} />}
      {dialog?.kind === "remind" && <ReminderDialog installment={dialog.installment} onOpenChange={close} onDone={done} />}
      {dialog?.kind === "credit" && <CreditNoteDialog invoice={dialog.invoice} onOpenChange={close} onDone={done} />}
      {dialog?.kind === "refund" && <RefundDialog payment={dialog.payment} onOpenChange={close} onDone={done} />}
      <ReasonDialog open={dialog?.kind === "cancel-installment"} title="Annuler cette échéance ?" confirm="Annuler l'échéance" danger
        description="Elle ne sera plus due (geste commercial). Elle reste visible, annulée, avec son motif." success="Échéance annulée"
        onOpenChange={(o) => !o && close()} onDone={reload} testid="billing-cancel-installment-dialog"
        onSubmit={(reason) => api.post(`/billing/installments/${dialog.installment.id}/cancel`, { expected_version: dialog.installment.version, reason })} />
      <ReasonDialog open={dialog?.kind === "cancel-document"} danger success="Document annulé" testid="billing-cancel-document-dialog"
        title={dialog?.document?.kind === "invoice" ? `Annuler la facture ${dialog?.document?.number} ?` : `Annuler l'avoir ${dialog?.document?.number} ?`}
        description={dialog?.document?.kind === "invoice" ? "Ses échéances redeviennent à facturer ; son numéro se libère." : "Ce qu'il retirait redevient dû."}
        confirm="Annuler" onOpenChange={(o) => !o && close()} onDone={reload}
        onSubmit={(reason) => api.post(`/billing/${dialog.document.kind === "invoice" ? "invoices" : "credit-notes"}/${dialog.document.id}/cancel`,
          { expected_version: dialog.document.version, reason })} />
      <ReasonDialog open={dialog?.kind === "cancel-payment"} title="Annuler cet encaissement ?" confirm="Annuler l'encaissement" danger
        description="Il ne comptera plus ; l'échéance redevient due. Consignez ensuite le bon encaissement si besoin." success="Encaissement annulé"
        onOpenChange={(o) => !o && close()} onDone={reload} testid="billing-cancel-payment-dialog"
        onSubmit={(reason) => api.post(`/billing/payments/${dialog.payment.id}/cancel`, { expected_version: dialog.payment.version, reason })} />
    </>
  );

  return { installmentActions, documentActions, paymentActions, dialogs, canInvoice, canPay, openInvoice: (items) => setDialog({ kind: "invoice", items }) };
}
